import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Video, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const FILTERS = [
  { id: 'all', label: 'Everyone' },
  { id: 'practice_admin', label: 'Practice owners' },
  { id: 'practice_staff', label: 'Staff' },
];
const fmt = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.round(s || 0) % 60).padStart(2, '0')}`;

const Stat = ({ label, value, sub, testId }) => (
  <div className="rounded-xl border bg-white p-4" data-testid={testId}>
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

export const WelcomeVideoStatsAdmin = ({ adminToken }) => {
  const [role, setRole] = useState('all');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/welcome-video-stats`, {
        params: { role }, headers: { Authorization: `Bearer ${adminToken || localStorage.getItem('adminToken')}` },
      });
      setData(res.data); setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load welcome video stats');
    }
  }, [role, adminToken]);
  useEffect(() => { load(); }, [load]);

  const s = data?.summary;
  const pct = (n) => (s?.shown ? `${Math.round((100 * n) / s.shown)}% of shown` : '');

  return (
    <Card data-testid="welcome-video-stats">
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="flex items-center gap-2"><Video className="w-5 h-5 text-emerald-600" />Welcome Video</CardTitle>
        <div className="flex items-center gap-2">
          {FILTERS.map((f) => (
            <Button key={f.id} size="sm" variant={role === f.id ? 'default' : 'outline'} onClick={() => setRole(f.id)}
              data-testid={`welcome-video-filter-${f.id}`}>{f.label}</Button>
          ))}
          <Button size="sm" variant="ghost" onClick={load} data-testid="welcome-video-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && <p className="text-red-600 text-sm" data-testid="welcome-video-error">{error}</p>}
        {s && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Shown" value={s.shown} testId="wv-stat-shown" />
            <Stat label="Pressed play" value={s.played} sub={pct(s.played)} testId="wv-stat-played" />
            <Stat label="Watched to end" value={s.completed} sub={pct(s.completed)} testId="wv-stat-completed" />
            <Stat label="Closed early" value={s.closedEarly} sub={s.closedEarly ? `left at ${fmt(s.avgStopSeconds)} on average` : ''} testId="wv-stat-closed-early" />
            <Stat label="Avg watched" value={`${s.avgPercent}%`} testId="wv-stat-avg-percent" />
          </div>
        )}
        {data && (data.rows.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="welcome-video-empty">No new users have seen the welcome video yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="welcome-video-table">
              <thead><tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-3">User</th><th className="py-2 pr-3">Practice</th><th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Watched</th><th className="py-2">Status</th>
              </tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.email} className="border-b last:border-0">
                    <td className="py-2 pr-3"><p className="font-medium text-gray-900">{r.name}</p><p className="text-xs text-gray-500">{r.email}</p></td>
                    <td className="py-2 pr-3">{r.practiceName}</td>
                    <td className="py-2 pr-3">{r.role === 'practice_staff' ? 'Staff' : 'Owner'}</td>
                    <td className="py-2 pr-3">{r.shownAt ? new Date(r.shownAt).toLocaleString() : ''}</td>
                    <td className="py-2 pr-3">{r.duration ? `${fmt(r.seconds)} of ${fmt(r.duration)} · ${r.percent}%` : `${fmt(r.seconds)}`}</td>
                    <td className="py-2">
                      {r.completed ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs">Finished</span>
                        : r.closedEarly ? (
                          <span className="flex flex-col gap-1">
                            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs w-fit">Closed early</span>
                            {r.followUpSentAt && <span className="text-xs text-blue-700" data-testid="wv-followup-sent">Follow-up sent {new Date(r.followUpSentAt).toLocaleDateString()}</span>}
                          </span>
                        )
                          : <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">{r.played ? 'Watching / left page' : 'Not played'}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
